import  React 
  from 'react';
import {Radar, 
        RadarChart, 
        PolarGrid, 
        PolarAngleAxis, 
        PolarRadiusAxis, 
        Tooltip, 
        ResponsiveContainer,
} from 'recharts';
import {
    COLORS,
} from "../";
import  styles 
  from './index.module.scss';


interface ScaledValueAttributes {
    min: number;
    max: number;
    value: number;
    scaleType: "linear" | "log" ;
}
interface DataStackedAttributes {
    subject: string;
    label?: string;
    scaledValues: ScaledValueAttributes[];
}
interface DataAttributes {
    subject: string;
    label?: string;
    scaledValue: ScaledValueAttributes;
}
interface RadarGraphProps{
    data: DataAttributes[];
}
interface RadarGraphStackedProps{
    data: DataStackedAttributes[];
}
export const RadarGraph: React.FC<RadarGraphProps> = ({
    data,
}) => {
    return <RadarGraphStacked
        data={data.map((item) => {
            return {
                subject: item.subject,
                label: item.label,
                scaledValues: [item.scaledValue],
            };
        })}
    />;
}
export const RadarGraphStacked: React.FC<RadarGraphStackedProps> = ({
    data,    
}) => {
    interface DataFlattenedAttributes {
        subject: string;
        label?: string;
        [key: `scaledValue${number}`] : ScaledValueAttributes | undefined;
    }
    const dataFlattened = data.map((item) => {
        let new_item : DataFlattenedAttributes = {
            subject: item.subject,
            label: item.label,
        };
        item.scaledValues.forEach((scaledValue, index) => {
            new_item[`scaledValue${index}`] = scaledValue;
        });
        return new_item;
    });
    

    return (
        <ResponsiveContainer 
            width={"100%"} 
            height={"100%"}
        >
            <RadarChart
                cx={"50%"}
                cy={"50%"}
                innerRadius={"0%"}
                outerRadius={"80%"}
                data={dataFlattened}
            >
                <PolarGrid 
                    gridType={"polygon"}
                    radialLines={true}
                />
                <PolarAngleAxis
                    dataKey={"subject"}
                    tick={false}
                />
                <PolarRadiusAxis
                    angle={360/ data.length}
                    domain={[0, 100]}
                    tick={false}
                    axisLine={false}
                />
                { dataFlattened && dataFlattened[0] && Object.keys(dataFlattened[0]).map((key) => {
                    if (key.includes("scaledValue")) {
                        let index = parseInt(key.replace("scaledValue", ""));
                        let color = COLORS[index % COLORS.length];
                        return <Radar 
                            name={key}
                            key={key}
                            dataKey={(data) => {
                                return calculateScaledValue(
                                    data[key].value,
                                    data[key].min,
                                    data[key].max,
                                    data[key].scaleType,
                                );
                            }}
                            stroke={color}
                            fill={color}
                            fillOpacity={0.5}
                        />;
                    }
                })}
                <Tooltip 
                    content={
                        ({payload}) => {return <div className={styles['label-container']}>
                            {payload?.map((item) => <>
                                <div className={styles['label-title']}>{item.payload.subject}</div>
                                <div className={styles['label-content']}>{
                                    item.payload.label ??
                                    item.value
                                }</div>
                            </>)}
                        </div>;
                        }
                    }
                />
            </RadarChart>
        </ResponsiveContainer>
    );
};


/**
 * Utils 
 */
const calculateScaledValue = (
    value: number, 
    min: number, 
    max: number,
    scaleType: "linear" | "log",
) => {
    if (scaleType === "linear") {
        return calculateLinearScaledValue(value, min, max);
    } else if (scaleType === "log") {
        return calculateLogScaledValue(value, min, max);
    }
}

const calculateLinearScaledValue = (
    value: number, 
    min: number, 
    max: number,
) => {
    if (value < min) {
        return 0;
    } else if (value > max) {
        return 100;
    } else {
        return ((value - min) / (max - min)) * 100;
    }
}

const calculateLogScaledValue = (
    value: number, 
    min: number, 
    max: number,
) => {
    if (value < min) {
        return 0;
    } else if (value > max) {
        return 100;
    } else {
        const scaledValue = ((Math.log(value) - Math.log(min)) / (Math.log(max) - Math.log(min))) * 100;
        return scaledValue;
    }
}

